import { useEval, DIMENSION_COLORS } from '@/context/EvalContext';
import { useRef, useState } from 'react';


function ScoreBadge({ s }: { s: number }) {
  const bg = s >= 93 ? 'rgba(34,211,238,0.16)' : s >= 90 ? 'rgba(16,185,129,0.14)' : 'rgba(251,191,36,0.16)';
  const color = s >= 93 ? '#22d3ee' : s >= 90 ? '#34d399' : '#fbbf24';
  return (
    <span className="rounded-md px-2 py-0.5 font-bold whitespace-nowrap flex-shrink-0"
      style={{ fontSize: '17px', background: bg, color, border: `1px solid ${color}35` }}>
      {s}
    </span>
  );
}

function ItemCard({ item }: { item: any }) {
  return (
    <div className="rounded-xl p-3 flex-shrink-0"
      style={{
        background: `linear-gradient(145deg, ${item.dimColor}10, rgba(0,0,0,0.18))`,
        border: `1px solid ${item.dimColor}28`,
      }}>
      {/* 维度标记 + 子维度名 + 分数 */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-1.5 h-1.5 rounded-full flex-shrink-0"
            style={{ background: item.dimColor, boxShadow: `0 0 6px ${item.dimColor}` }} />
          <span className="text-base font-semibold whitespace-nowrap"
            style={{ color: item.dimColor, fontSize: '17px' }}>
            {item.dimName}
          </span>
          <span className="font-semibold text-white font-bold truncate" style={{ fontSize: '17px' }}>
            {item.name}
          </span>
        </div>
        <ScoreBadge s={item.score} />
      </div>
      {/* 描述 */}
      <p style={{ fontSize: '16px', color: 'rgba(203,213,225,0.68)', lineHeight: 1.7, margin: 0 }}>
        {item.description}
      </p>
    </div>
  );
}

export default function ImprovementPanel() {
  const evalData = useEval();
  const [paused, setPaused] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  
  if (!evalData) return null;
  const dimensions = evalData.courseEvalData.improvements?.dimensions || [];
  const allItems = dimensions.flatMap((dim: any) =>
    dim.subItems.map((sub: any) => {
      const dimColor = DIMENSION_COLORS[dim.id] || '#22d3ee';
      return { ...sub, dimName: dim.name, dimColor };
    })
  );
  
  // 动画速度：每张卡约 4s，循环一轮 = allItems.length * 4s
  const animDuration = allItems.length * 4;

  return (
    <div className="h-full flex flex-col" style={{ padding: '4px 8px 6px' }}>
      {/* 标题 + 总分 */}
      <div className="flex items-center justify-between px-1 mb-2.5 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-1 h-4 rounded-full flex-shrink-0"
            style={{ background: 'linear-gradient(to bottom, #22d3ee, rgba(34,211,238,0.5))', boxShadow: '0 0 10px rgba(34,211,238,0.9)' }} />
          <span className="text-white font-bold font-semibold tracking-wide" style={{ fontSize: '22px' }}>改进建议</span>
        </div>

      </div>

      {/* 滚动播放区域 */}
      <div
        className="flex-1 min-h-0 relative overflow-hidden"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* 顶部渐变遮罩 */}
        <div className="absolute top-0 left-0 right-0 z-10 pointer-events-none" style={{ height: '32px', background: 'linear-gradient(to bottom, rgba(4,14,30,0.95), transparent)' }} />
        {/* 底部渐变遮罩 */}
        <div className="absolute bottom-0 left-0 right-0 z-10 pointer-events-none" style={{ height: '32px', background: 'linear-gradient(to top, rgba(4,14,30,0.95), transparent)' }} />

        {/* 滚动轨道：内容复制两份实现无缝循环 */}
        <div
          ref={trackRef}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            height: 'max-content', // 确保轨道真实高度被撑开而不是被外层截断
            animation: `improveScroll ${animDuration}s linear infinite`,
            animationPlayState: paused ? 'paused' : 'running',
            paddingBottom: '8px',
          }}
        >
          {/* 第一份 */}
          {allItems.map((item, i) => (
            <ItemCard key={`a-${i}`} item={item} />
          ))}
          {/* 第二份（无缝衔接） */}
          {allItems.map((item, i) => (
            <ItemCard key={`b-${i}`} item={item} />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes improveScroll {
          0%   { transform: translateY(0); }
          100% { transform: translateY(-50%); }
        }
      `}</style>
    </div>
  );
}
